/**
 * Ключ обложки страницы новой версии — общий для сервера (перенос из старого
 * хранилища, lib/flow-page) и клиента (редактор, app/flow-test/FlowEditorDoc).
 *
 * Вынесен отдельным файлом намеренно: lib/flow-page тянет БД, и импорт его в
 * клиентский компонент утащил бы better-sqlite3 в браузерную сборку.
 *
 * Почему обложки вообще живут отдельным ключом: пользователь грузит свои
 * картинки, они хранятся как data-URL и весят под сотню килобайт (в старом
 * документе прототипа именно они занимали 96% веса). Класть такое в строку
 * страницы нельзя — раздует список страниц. Правильное место — файл на диске,
 * это отдельный шаг.
 */
export const coverKeyOf = (pageId: string) => `flowtest:cover:${pageId}`;

/** Color covers share the existing page setting, but never become image URLs
 * or arbitrary CSS. Presets and strictly six-digit template colors stay small. */
export const FLOW_COVER_COLORS = [
  { value: "color:sage", label: "Шалфей", color: "#DCE7DC" },
  { value: "color:mist", label: "Туман", color: "#E3E5E3" },
  { value: "color:sand", label: "Песок", color: "#EAE1D1" },
  { value: "color:sky", label: "Небо", color: "#DCE7EF" },
  { value: "color:lavender", label: "Лаванда", color: "#E4DFF0" },
  { value: "color:rose", label: "Роза", color: "#EEDDDD" },
  { value: "color:peach", label: "Персик", color: "#F0DFD0" },
  { value: "color:olive", label: "Олива", color: "#E4E6D4" },
] as const;

export const FLOW_COVER_GRADIENTS = FLOW_COVER_COLORS.map(option => ({
  ...option, value: `/flow-covers/gradient-${option.value.slice("color:".length)}.svg`,
}));

/** Decorative gradients stay behind the normal header; only photo covers
 * reserve extra space. Match preset URLs exactly, not user image filenames. */
export function isFlowGradientCover(cover: string | undefined | null): boolean {
  return FLOW_COVER_GRADIENTS.some(option => option.value === cover);
}

export function flowCoverColor(cover: string | undefined | null): string | undefined {
  return FLOW_COVER_COLORS.find(option => option.value === cover)?.color
    ?? (typeof cover === "string" && /^color:#[0-9a-f]{6}$/i.test(cover) ? cover.slice(6) : undefined);
}

export function readFlowCover(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  try {
    const cover: unknown = JSON.parse(value);
    return typeof cover === "string" && cover.length > 0
      && (!cover.startsWith("color:") || !!flowCoverColor(cover))
      && (!cover.startsWith("banner:") || !!flowCoverBannerImage(cover)) ? cover : undefined;
  } catch { return undefined; }
}

/** A photo cropped into the same panel as a color; it is deliberately
 * distinct from the historical full-width scenic background image. */
export function flowCoverBannerImage(cover: string | undefined | null): string | undefined {
  if (typeof cover !== "string" || !cover.startsWith("banner:")) return undefined;
  const image = cover.slice(7);
  return /^\/flow-covers-user\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\.(?:jpg|png|webp)$/.test(image) ? image : undefined;
}
export function isFlowPanelCover(cover: string | undefined | null): boolean {
  return !!flowCoverColor(cover) || !!flowCoverBannerImage(cover);
}
export function flowCoverImage(cover: string | undefined | null): string | undefined {
  return flowCoverBannerImage(cover) ?? (cover && !flowCoverColor(cover) && !cover.startsWith("banner:") ? cover : undefined);
}
